<template>
  <div class="cp-pagina">
    <div class="card">

      <div class="cp-testa">
        <div>
          <h5 class="mb-1">Controllo pagamenti</h5>
          <p class="cp-intro">
            Verifica che i movimenti di un conto abbiano attaccati i pagamenti giusti. Non modifica nulla.
          </p>
        </div>
        <Button v-if="isAdmin" label="Registro attività" icon="pi pi-history"
          class="p-button-outlined p-button-secondary" @click="apriRegistro" />
      </div>

      <div class="cp-comandi">
        <div class="cp-campo">
          <label for="cp-conto">Conto</label>
          <Dropdown id="cp-conto" v-model="conto" :options="gruppiConti" optionLabel="nome" optionValue="id"
            optionGroupLabel="etichetta" optionGroupChildren="voci" :filter="true" filterPlaceholder="Cerca"
            placeholder="Scegli il conto" :loading="contiInCaricamento" class="cp-conto" />
        </div>
        <div class="cp-campo">
          <label for="cp-periodo">Periodo</label>
          <Calendar id="cp-periodo" v-model="periodo" dateFormat="dd/mm/yy" selectionMode="range"
            :manualInput="false" placeholder="Dal / al" showButtonBar class="cp-periodo" />
        </div>
        <Button label="Controlla" icon="pi pi-search" class="p-button-success" :loading="inCorso"
          @click="esegui" />
      </div>

      <!-- Mai eseguito -->
      <div v-if="!esito && !inCorso" class="cp-inizio">
        <p>Scegli un conto e un periodo per iniziare.</p>
        <p class="cp-inizio-alt">
          Arrivando dalla pagina Controlli Estratti Conto il periodo viene compilato da solo.
        </p>
      </div>

      <div v-else-if="inCorso" class="cp-attesa">
        <Skeleton width="14rem" height="2rem" class="mb-3" />
        <Skeleton width="22rem" height="3.5rem" class="mb-4" />
        <Skeleton height="1.2rem" class="mb-2" v-for="n in 5" :key="n" />
      </div>

      <!-- Esito -->
      <template v-else>
        <div class="cp-testata">
          <div>
            <div class="cp-testata-conto">{{ esito.conto }}</div>
            <div class="cp-testata-meta">
              {{ formatDate(esito.periodo.dal) }} - {{ formatDate(esito.periodo.al) }},
              {{ formatNumero(esito.totali.movimenti) }} movimenti
            </div>
          </div>
          <Button label="Esporta in Excel" icon="pi pi-file-excel" class="p-button-sm p-button-outlined"
            @click="esporta" />
        </div>

        <div v-if="!tuttoOk" class="cp-verdetto">
          <div class="cp-cifra">{{ verdetto.cifra }}</div>
          <div class="cp-cifra-l">{{ verdetto.etichetta }}</div>
        </div>
        <div v-else class="cp-pulito">
          <i class="pi pi-check-circle"></i>
          <div>
            <div class="cp-pulito-t">I pagamenti tornano</div>
            <div class="cp-pulito-s">
              Nessun errore certo su {{ formatNumero(esito.totali.movimenti) }} movimenti.
            </div>
          </div>
        </div>

        <div v-if="esito.troncato" class="cp-nota">
          Elenchi limitati alle prime {{ esito.max_dettaglio }} righe. Conteggi e totali restano completi.
        </div>

        <TabView v-if="viste.length" v-model:activeIndex="vista" :lazy="true" class="cp-viste">
          <TabPanel v-for="v in viste" :key="v.chiave">
            <template #header>
              <span class="cp-tab-t">{{ v.titolo }}</span>
              <span class="cp-tab-n" :class="{ 'cp-tab-n-grave': v.grave }">{{ formatNumero(v.n) }}</span>
            </template>

            <p class="cp-spiega">{{ v.spiegazione }}</p>

            <DataTable v-if="v.chiave === 'ripetute'" :value="esito.fatture_ripetute"
              responsiveLayout="scroll" class="p-datatable-sm" v-bind="pag(esito.fatture_ripetute)">
              <Column header="Data" style="width:7rem" bodyClass="cp-nowrap">
                <template #body="{ data }">{{ formatDate(data.data_contabile) }}</template>
              </Column>
              <Column field="numero" header="Fattura" style="width:8rem" bodyClass="cp-nowrap cp-mono" />
              <Column header="Pagamenti" style="width:6.5rem" bodyClass="text-center">
                <template #body="{ data }"><span class="cp-difetto">{{ data.volte }}</span></template>
              </Column>
              <Column header="Movimento" style="width:9.5rem" bodyClass="text-right cp-nowrap cp-mono">
                <template #body="{ data }">{{ fmtEur(data.importo_movimento) }}</template>
              </Column>
              <Column header="Pagato" style="width:9.5rem" bodyClass="text-right cp-nowrap cp-mono">
                <template #body="{ data }">{{ fmtEur(data.totale_pagato) }}</template>
              </Column>
              <Column field="descrizione" header="Descrizione" bodyClass="cp-desc-cell">
                <template #body="{ data }"><span class="cp-desc" :title="data.descrizione">{{ data.descrizione }}</span></template>
              </Column>
              <Column header="" style="width:6rem" bodyClass="cp-nowrap">
                <template #body="{ data }">
                  <Button icon="pi pi-credit-card" class="p-button-sm p-button-text"
                    v-tooltip.top="'Apri il movimento in estratto conto'" @click="apriMovimento(data.id_estrattoconto)" />
                  <Button icon="pi pi-file" class="p-button-sm p-button-text"
                    v-tooltip.top="'Apri la fattura'" @click="apriFattura(data.numero)" />
                </template>
              </Column>
            </DataTable>

            <DataTable v-else-if="v.chiave === 'sovrapagate'" :value="esito.fatture_sovrapagate"
              responsiveLayout="scroll" class="p-datatable-sm" v-bind="pag(esito.fatture_sovrapagate)">
              <Column field="numero" header="Fattura" style="width:9rem" bodyClass="cp-nowrap cp-mono" />
              <Column header="Totale fattura" style="width:10rem" bodyClass="text-right cp-nowrap cp-mono">
                <template #body="{ data }">{{ fmtEur(data.tot_fattura) }}</template>
              </Column>
              <Column header="Pagato" style="width:10rem" bodyClass="text-right cp-nowrap cp-mono">
                <template #body="{ data }">{{ fmtEur(data.pagato) }}</template>
              </Column>
              <Column header="In eccesso" style="width:10rem" bodyClass="text-right cp-nowrap cp-mono">
                <template #body="{ data }"><span class="cp-difetto">{{ fmtEur(data.eccesso) }}</span></template>
              </Column>
              <Column header="Pagamenti" bodyClass="cp-nowrap">
                <template #body="{ data }">
                  {{ data.n_pagamenti }} su {{ data.n_movimenti }}
                  {{ data.n_movimenti === 1 ? 'movimento' : 'movimenti' }}
                </template>
              </Column>
              <Column header="" style="width:3.5rem">
                <template #body="{ data }">
                  <Button icon="pi pi-file" class="p-button-sm p-button-text"
                    v-tooltip.top="'Apri la fattura'" @click="apriFattura(data.numero)" />
                </template>
              </Column>
            </DataTable>

            <DataTable v-else-if="v.chiave === 'scarto'" :value="esito.movimenti_con_scarto"
              responsiveLayout="scroll" class="p-datatable-sm" v-bind="pag(esito.movimenti_con_scarto)">
              <Column header="Data" style="width:7rem" bodyClass="cp-nowrap">
                <template #body="{ data }">{{ formatDate(data.data_contabile) }}</template>
              </Column>
              <Column header="Movimento" style="width:9.5rem" bodyClass="text-right cp-nowrap cp-mono">
                <template #body="{ data }">{{ fmtEur(data.importo) }}</template>
              </Column>
              <Column header="Pagato" style="width:9.5rem" bodyClass="text-right cp-nowrap cp-mono">
                <template #body="{ data }">{{ fmtEur(data.pagato) }}</template>
              </Column>
              <Column header="Scarto" style="width:9.5rem" bodyClass="text-right cp-nowrap cp-mono">
                <template #body="{ data }"><span class="cp-difetto">{{ fmtEur(data.scarto) }}</span></template>
              </Column>
              <Column field="descrizione" header="Descrizione" bodyClass="cp-desc-cell">
                <template #body="{ data }"><span class="cp-desc" :title="data.descrizione">{{ data.descrizione }}</span></template>
              </Column>
              <Column header="" style="width:3.5rem">
                <template #body="{ data }">
                  <Button icon="pi pi-credit-card" class="p-button-sm p-button-text"
                    v-tooltip.top="'Apri il movimento in estratto conto'" @click="apriMovimento(data.id, data.contabilizzato)" />
                </template>
              </Column>
            </DataTable>

            <div v-else class="cp-ripartizione">
              <button v-for="t in esito.senza_pagamenti" :key="t.cod_type_pay" type="button"
                class="cp-quota" @click="apriSenzaPagamenti(t)">
                <span class="cp-quota-n">{{ formatNumero(t.movimenti) }}</span>
                <span class="cp-quota-t">
                  {{ t.tipo }}
                  <span class="cp-quota-e">{{ fmtEur(t.importo) }}</span>
                </span>
                <i class="pi pi-angle-right cp-quota-i"></i>
              </button>
            </div>
          </TabPanel>
        </TabView>
      </template>

    </div>

    <Dialog v-model:visible="dettaglioDialog" :modal="true" class="cp-dialog"
      :style="{ width: '92%', maxWidth: '1000px' }" :header="dettaglioTitolo">
      <div v-if="dettaglioInCorso" class="pt-2">
        <Skeleton height="1.2rem" class="mb-2" v-for="n in 6" :key="n" />
      </div>
      <template v-else>
        <p v-if="dettaglio.troncato" class="cp-nota pt-0">
          Elenco limitato ai primi {{ dettaglio.max_dettaglio }} movimenti.
        </p>
        <DataTable :value="dettaglio.movimenti" responsiveLayout="scroll" class="p-datatable-sm"
          v-bind="pag(dettaglio.movimenti)">
          <Column header="Data" style="width:7rem" bodyClass="cp-nowrap">
            <template #body="{ data }">{{ formatDate(data.data_contabile) }}</template>
          </Column>
          <Column header="Importo" style="width:10rem" bodyClass="text-right cp-nowrap cp-mono">
            <template #body="{ data }">{{ fmtEur(data.importo) }}</template>
          </Column>
          <Column field="descrizione" header="Descrizione" bodyClass="cp-desc-cell">
            <template #body="{ data }"><span class="cp-desc" :title="data.descrizione">{{ data.descrizione }}</span></template>
          </Column>
          <Column header="" style="width:3.5rem">
            <template #body="{ data }">
              <Button icon="pi pi-credit-card" class="p-button-sm p-button-text"
                v-tooltip.top="'Apri il movimento in estratto conto'" @click="apriMovimento(data.id)" />
            </template>
          </Column>
        </DataTable>
      </template>
    </Dialog>

    <Dialog v-model:visible="registroDialog" :modal="true" :maximizable="true" class="cp-registro"
      :style="{ width: 'auto' }" header="Registro attività">
      <p class="cp-registro-sub">Chi ha lanciato un controllo e con che esito, dal più recente.</p>
      <DataTable :value="registro" :loading="registroInCaricamento" responsiveLayout="scroll" class="p-datatable-sm"
        :paginator="registro.length > 15" :rows="15" :rowsPerPageOptions="[15, 25, 50, 100]"
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="Visualizzate {first}-{last} di {totalRecords}">
        <template #empty><span class="cp-registro-sub">Nessuna attività registrata</span></template>
        <Column header="Quando">
          <template #body="{ data }">{{ formatDateTime(data.quando) }}</template>
        </Column>
        <Column header="Chi">
          <template #body="{ data }">{{ data.utente || 'utente ignoto' }}</template>
        </Column>
        <Column header="Azione">
          <template #body="{ data }">
            <span class="cp-pill" :class="data.azione === 'controllo_pagamenti_errore' ? 'cp-pill-ko' : 'cp-pill-ok'">
              {{ etichettaAzione(data.azione) }}
            </span>
          </template>
        </Column>
        <Column header="Conto">
          <template #body="{ data }">{{ (data.dettagli && data.dettagli.conto) || '-' }}</template>
        </Column>
        <Column header="Periodo">
          <template #body="{ data }">{{ periodoRegistro(data) }}</template>
        </Column>
        <Column header="Esito">
          <template #body="{ data }">{{ esitoRegistro(data) }}</template>
        </Column>
      </DataTable>
      <template #footer>
        <Button label="Chiudi" icon="pi pi-times" class="p-button-text" @click="registroDialog = false" />
      </template>
    </Dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useRoute } from 'vue-router'
import moment from 'moment'
import * as XLSX from 'xlsx'
import Service from '@/services/ask-pratiche/pratiche-service'

export default {
  name: 'ControlloPagamenti',

  setup() {
    const self = getCurrentInstance().appContext.config.globalProperties
    const service = new Service(self.$http, self.$eventBus)
    const toast = useToast()
    const route = useRoute()

    const formatDate = (v) => (v ? moment(v).format('DD/MM/YYYY') : '-')
    const formatDateTime = (v) => (v ? moment(v).format('DD/MM/YYYY HH:mm') : '-')
    const formatNumero = (n) => (n === null || n === undefined ? '-' : Number(n).toLocaleString('it-IT'))
    const fmtEur = (v) => {
      if (v === null || v === undefined || v === '') return '-'
      const n = Number(v)
      if (isNaN(n)) return '-'
      return n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
    }

    const conti = ref([])
    const contiInCaricamento = ref(false)
    const conto = ref(null)
    const periodo = ref(null)
    const esito = ref(null)
    const inCorso = ref(false)
    const vista = ref(0)
    const dettaglioDialog = ref(false)
    const dettaglioInCorso = ref(false)
    const dettaglioTitolo = ref('')
    const dettaglio = ref({ movimenti: [], troncato: false, max_dettaglio: 0 })
    const registroDialog = ref(false)
    const registro = ref([])
    const registroInCaricamento = ref(false)

    // Stesso criterio del registro in Controlli Estratti Conto: solo il profilo con `all = 1`.
    const isAdmin = computed(() => {
      try {
        return Number(JSON.parse(atob(localStorage.getItem('user'))).user[0].profile.all) === 1
      } catch (e) {
        return false
      }
    })

    // In ordine alfabetico le banche finiscono sparse tra una ventina di conti di costo.
    const gruppiConti = computed(() => {
      const gruppi = []
      const banche = conti.value.filter(c => c.banca)
      const altri = conti.value.filter(c => !c.banca)
      if (banche.length) gruppi.push({ etichetta: 'Banche', voci: banche })
      if (altri.length) gruppi.push({ etichetta: 'Altri conti', voci: altri })
      return gruppi
    })

    // Le schede che la contabilita' non usa restano calcolate ma fuori dalla pagina:
    // per rimetterle basta la chiave qui, e riappaiono anche nel verdetto e nell'export.
    const VISTE_ATTIVE = ['ripetute', 'scarto']
    const attiva = (chiave) => VISTE_ATTIVE.indexOf(chiave) !== -1

    // Il verdetto conta solo cio' che si puo' aprire: una cifra senza la scheda che la
    // spiega non e' verificabile da chi la legge.
    const erroriCerti = computed(() => {
      if (!esito.value) return 0
      const t = esito.value.totali
      return (attiva('ripetute') ? t.fatture_ripetute : 0)
        + (attiva('sovrapagate') ? t.fatture_sovrapagate : 0)
    })
    const tuttoOk = computed(() => erroriCerti.value === 0)

    // Con eccesso a zero la cifra in evidenza sarebbe uno 0,00: si mostra il conteggio.
    const verdetto = computed(() => {
      const righe = formatNumero(erroriCerti.value) + ' '
        + (erroriCerti.value === 1 ? 'riga da correggere' : 'righe da correggere')
      const eccesso = attiva('sovrapagate') ? Number(esito.value?.totali.eccesso_euro || 0) : 0

      return eccesso > 0
        ? { cifra: fmtEur(eccesso), etichetta: 'pagati in eccesso, su ' + righe }
        : { cifra: formatNumero(erroriCerti.value), etichetta: righe.replace(/^\S+ /, '') }
    })

    const viste = computed(() => {
      if (!esito.value) return []
      const e = esito.value
      const v = []
      if (e.fatture_ripetute.length) v.push({
        chiave: 'ripetute', titolo: 'Pagamenti ripetuti', n: e.totali.fatture_ripetute, grave: true,
        spiegazione: 'Sullo stesso movimento la stessa fattura ha più righe di pagamento, '
          + 'mentre un movimento la paga una volta sola. Gli storni sono già scalati.',
      })
      if (e.fatture_sovrapagate.length) v.push({
        chiave: 'sovrapagate', titolo: 'Fatture sovrapagate', n: e.totali.fatture_sovrapagate, grave: true,
        spiegazione: 'I pagamenti attaccati alla fattura superano il totale della fattura.',
      })
      if (e.movimenti_con_scarto.length) v.push({
        chiave: 'scarto', titolo: 'Da guardare', n: e.totali.movimenti_con_scarto, grave: false,
        spiegazione: 'La somma dei pagamenti non fa l\'importo del movimento. Comprende le fatture ripetute.',
      })
      if (e.senza_pagamenti.length) v.push({
        chiave: 'senza', titolo: 'Senza pagamenti', n: e.totali.senza_pagamenti, grave: false,
        spiegazione: 'Movimenti già contabilizzati a cui non è attaccato nessun pagamento.',
      })
      return v.filter(x => attiva(x.chiave))
    })

    const pag = (righe) => ({
      paginator: righe.length > 8,
      rows: 8,
      rowsPerPageOptions: [8, 25, 50],
      paginatorTemplate: 'CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown',
      currentPageReportTemplate: 'Visualizzate {first}-{last} di {totalRecords}',
    })

    const caricaConti = async () => {
      contiInCaricamento.value = true
      try {
        const res = await service.getContiControlloPagamenti()
        conti.value = res.conti || []
      } finally {
        contiInCaricamento.value = false
      }
    }

    const esegui = async () => {
      if (!conto.value) {
        toast.add({ severity: 'warn', detail: 'Scegli il conto da controllare', life: 4000 })
        return
      }
      const r = periodo.value
      if (!r || r.length !== 2 || !r[0] || !r[1]) {
        toast.add({ severity: 'warn', detail: 'Indica il periodo', life: 4000 })
        return
      }

      inCorso.value = true
      vista.value = 0
      try {
        esito.value = await service.eseguiControlloPagamenti({
          codTypePayMode: conto.value,
          dal: moment(r[0]).format('YYYY-MM-DD'),
          al: moment(r[1]).format('YYYY-MM-DD'),
        })
      } catch (error) {
        const status = error?.response?.status
        const messaggi = error?.response?.data
        if (status === 422 && typeof messaggi === 'object') {
          Object.keys(messaggi).forEach(k => {
            const v = Array.isArray(messaggi[k]) ? messaggi[k] : [messaggi[k]]
            v.forEach(m => toast.add({ severity: 'warn', summary: 'Attenzione', detail: m, life: 8000 }))
          })
        } else {
          toast.add({ severity: 'error', summary: 'Controllo non riuscito',
            detail: messaggi?.message || error?.message || 'Errore imprevisto', life: 8000 })
        }
      } finally {
        inCorso.value = false
      }
    }

    // Il dettaglio ha due rotte distinte a seconda che il movimento sia gia' contabilizzato.
    const apriMovimento = (id, contabilizzato = true) => {
      const rotta = contabilizzato ? 'regularized' : 'not-regularized'
      window.open(`/estratti-conto/detail/${rotta}/${id}`, '_blank')
    }

    const apriFattura = (numero) => {
      if (numero) window.open(`/ask/clienti/fatture/${numero}/dettagli`, '_blank')
    }

    const apriRegistro = async () => {
      registroDialog.value = true
      registroInCaricamento.value = true
      try {
        registro.value = (await service.getAuditControlloPagamenti()).movimenti || []
      } finally {
        registroInCaricamento.value = false
      }
    }

    const etichettaAzione = (a) => ({
      controllo_pagamenti: 'Esecuzione',
      controllo_pagamenti_errore: 'Errore',
    }[a] || a)

    const periodoRegistro = (riga) => {
      const d = riga.dettagli || {}
      return d.dal && d.al ? `${formatDate(d.dal)} - ${formatDate(d.al)}` : '-'
    }

    // Le stesse schede della pagina: una scheda nascosta non compare neanche qui.
    const esitoRegistro = (riga) => {
      const d = riga.dettagli || {}
      if (riga.azione === 'controllo_pagamenti_errore') return d.errore || 'errore sconosciuto'
      const t = d.totali
      if (!t) return '-'
      const parti = [formatNumero(t.movimenti) + ' movimenti']
      if (attiva('ripetute')) parti.push(formatNumero(t.fatture_ripetute) + ' pagamenti ripetuti')
      if (attiva('sovrapagate')) parti.push(formatNumero(t.fatture_sovrapagate) + ' sovrapagate')
      if (attiva('scarto')) parti.push(formatNumero(t.movimenti_con_scarto) + ' da guardare')
      if (attiva('senza')) parti.push(formatNumero(t.senza_pagamenti) + ' senza pagamenti')
      return parti.join(', ')
    }

    const apriSenzaPagamenti = async (tipo) => {
      dettaglioTitolo.value = `${tipo.tipo} - ${formatNumero(tipo.movimenti)} `
        + (tipo.movimenti === 1 ? 'movimento' : 'movimenti')
      dettaglio.value = { movimenti: [], troncato: false, max_dettaglio: 0 }
      dettaglioDialog.value = true
      dettaglioInCorso.value = true

      try {
        dettaglio.value = await service.getMovimentiSenzaPagamenti({
          codTypePayMode: esito.value.cod_type_pay_mode || conto.value,
          dal: esito.value.periodo.dal,
          al: esito.value.periodo.al,
          codTypePay: tipo.cod_type_pay,
        })
      } catch (error) {
        dettaglioDialog.value = false
        toast.add({ severity: 'error', summary: 'Dettaglio non disponibile',
          detail: error?.response?.data?.errore?.[0] || error?.message || 'Errore imprevisto', life: 8000 })
      } finally {
        dettaglioInCorso.value = false
      }
    }

    const esporta = () => {
      if (!esito.value) return
      const r = esito.value
      const wb = XLSX.utils.book_new()

      const riepilogo = [
        { Voce: 'Conto', Valore: r.conto },
        { Voce: 'Periodo dal', Valore: r.periodo.dal },
        { Voce: 'Periodo al', Valore: r.periodo.al },
        { Voce: 'Movimenti analizzati', Valore: r.totali.movimenti },
      ]
      // Il file dice quello che dice la pagina: una scheda nascosta non esce in Excel.
      if (attiva('ripetute')) {
        riepilogo.push({ Voce: 'Fatture con pagamenti ripetuti sullo stesso movimento', Valore: r.totali.fatture_ripetute })
        riepilogo.push({ Voce: 'Pagamenti in eccesso', Valore: r.totali.pagamenti_in_eccesso })
      }
      if (attiva('sovrapagate')) {
        riepilogo.push({ Voce: 'Fatture pagate oltre il totale', Valore: r.totali.fatture_sovrapagate })
        riepilogo.push({ Voce: 'Eccesso in euro', Valore: r.totali.eccesso_euro })
      }
      if (attiva('scarto')) {
        riepilogo.push({ Voce: 'Movimenti con scarto', Valore: r.totali.movimenti_con_scarto })
      }
      if (attiva('senza')) {
        riepilogo.push({ Voce: 'Contabilizzati senza pagamenti', Valore: r.totali.senza_pagamenti })
      }
      riepilogo.push({ Voce: 'Controllo eseguito il', Valore: r.controllato_il })

      const vuoto = [{ Esito: 'Nessuna riga' }]
      const foglio = (chiave, nome, righe) => {
        if (!attiva(chiave)) return
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(righe.length ? righe : vuoto), nome)
      }

      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(riepilogo), 'Riepilogo')
      foglio('ripetute', 'Pagamenti ripetuti', r.fatture_ripetute)
      foglio('sovrapagate', 'Sovrapagate', r.fatture_sovrapagate)
      foglio('scarto', 'Con scarto', r.movimenti_con_scarto)
      foglio('senza', 'Senza pagamenti', r.senza_pagamenti)

      XLSX.writeFile(wb, `Pagamenti_${r.conto}_${r.periodo.dal}_${r.periodo.al}.xlsx`.replace(/\s+/g, '_'))

      if (r.troncato) {
        toast.add({ severity: 'warn', summary: 'Attenzione',
          detail: `Gli elenchi sono limitati alle prime ${r.max_dettaglio} righe, quindi anche il file esportato lo è.`,
          life: 8000 })
      }
    }

    onMounted(async () => {
      await caricaConti()

      // Si puo' arrivare qui dal controllo degli estratti conto, che passa conto e
      // periodo gia' verificati: in quel caso parte da solo.
      const q = route.query
      if (q.conto && q.dal && q.al) {
        conto.value = Number(q.conto)
        periodo.value = [moment(q.dal).toDate(), moment(q.al).toDate()]
        esegui()
      }
    })

    return {
      conti, contiInCaricamento, gruppiConti, conto, periodo, esito, inCorso,
      vista, viste, pag, erroriCerti, tuttoOk, verdetto, esegui, apriMovimento, apriFattura, apriSenzaPagamenti, esporta,
      dettaglioDialog, dettaglioInCorso, dettaglioTitolo, dettaglio,
      isAdmin, registroDialog, registro, registroInCaricamento, apriRegistro,
      etichettaAzione, periodoRegistro, esitoRegistro,
      formatDate, formatDateTime, formatNumero, fmtEur,
    }
  },
}
</script>

<style>
/* --cp-difetto e' riservato alla cella che segnala l'errore, non agli importi. */
.cp-pagina,
.cp-dialog,
.cp-registro {
  --cp-difetto: #9c4b4b;
  --cp-testo-quieto: #6b7280;
  --cp-linea: rgba(51, 51, 51, .1);
}

.cp-pagina .cp-testa {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.cp-pagina .cp-intro {
  color: var(--cp-testo-quieto);
  font-size: .875rem;
  margin: 0 0 1.75rem;
}

.cp-pagina .cp-comandi {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 1rem;
  padding-bottom: 1.75rem;
  border-bottom: 1px solid var(--cp-linea);
}

.cp-pagina .cp-campo > label {
  display: block;
  font-weight: 600;
  font-size: .85rem;
  margin-bottom: .4rem;
}

.cp-pagina .cp-conto { min-width: 16rem; }
.cp-pagina .cp-periodo { min-width: 18rem; }

/* Stati prima del risultato */
.cp-pagina .cp-inizio {
  padding: 4rem 0 3rem;
}

.cp-pagina .cp-inizio p {
  margin: 0;
  font-size: 1.05rem;
}

.cp-pagina .cp-inizio-alt {
  margin-top: .5rem !important;
  font-size: .875rem !important;
  color: var(--cp-testo-quieto);
}

.cp-pagina .cp-attesa {
  padding: 2.5rem 0;
  max-width: 46rem;
}

/* Testata del risultato */
.cp-pagina .cp-testata {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
  flex-wrap: wrap;
  padding: 2rem 0 1.25rem;
}

.cp-pagina .cp-testata-conto {
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.2;
}

.cp-pagina .cp-testata-meta {
  font-size: .875rem;
  color: var(--cp-testo-quieto);
  margin-top: .2rem;
  font-variant-numeric: tabular-nums;
}

.cp-pagina .cp-verdetto {
  padding: 1.25rem 0 1.75rem;
  border-top: 2px solid var(--cp-difetto);
}

.cp-pagina .cp-cifra {
  font-size: 2.4rem;
  font-weight: 700;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}

.cp-pagina .cp-cifra-l {
  font-size: .95rem;
  color: var(--cp-testo-quieto);
  margin-top: .25rem;
}

.cp-pagina .cp-pulito {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 0 1.75rem;
  border-top: 2px solid #8fcfa8;
}

.cp-pagina .cp-pulito .pi {
  font-size: 1.8rem;
  color: #3a7a55;
}

.cp-pagina .cp-pulito-t {
  font-size: 1.15rem;
  font-weight: 700;
  color: #3a7a55;
}

.cp-pagina .cp-pulito-s {
  font-size: .875rem;
  color: var(--cp-testo-quieto);
  margin-top: .1rem;
}

.cp-pagina .cp-nota,
.cp-dialog .cp-nota {
  font-size: .8rem;
  color: var(--cp-testo-quieto);
  padding-bottom: 1rem;
}

.cp-pagina .cp-viste .p-tabview-nav { border-bottom: 1px solid var(--cp-linea); }

.cp-pagina .cp-viste .p-tabview-nav-link {
  padding: .85rem 1.15rem;
  gap: .5rem;
}

.cp-pagina .cp-tab-t { font-size: .92rem; }

.cp-pagina .cp-tab-n {
  font-size: .78rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--cp-testo-quieto);
  background: rgba(51, 51, 51, .07);
  border-radius: 999px;
  padding: .1rem .45rem;
}

.cp-pagina .cp-tab-n-grave {
  color: var(--cp-difetto);
  background: rgba(156, 75, 75, .1);
}

.cp-pagina .cp-viste .p-tabview-panels { padding: 1.25rem 0 0; }

.cp-pagina .cp-spiega {
  font-size: .85rem;
  color: var(--cp-testo-quieto);
  line-height: 1.55;
  margin: 0 0 1.1rem;
}

.cp-pagina .cp-ripartizione {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
  gap: .35rem 2rem;
}

.cp-pagina .cp-quota {
  display: flex;
  align-items: center;
  gap: .7rem;
  width: 100%;
  padding: .6rem .5rem;
  border: 0;
  border-bottom: 1px solid var(--cp-linea);
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.cp-pagina .cp-quota:hover { background: rgba(51, 51, 51, .04); }

.cp-pagina .cp-quota-n {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  min-width: 3.5rem;
  text-align: right;
}

.cp-pagina .cp-quota-t {
  flex: 1;
  font-size: .85rem;
  color: #333;
  line-height: 1.35;
}

.cp-pagina .cp-quota-e {
  display: block;
  font-variant-numeric: tabular-nums;
  color: var(--cp-testo-quieto);
}

.cp-pagina .cp-quota-i {
  font-size: .8rem;
  color: var(--cp-testo-quieto);
}

.cp-pagina .cp-mono,
.cp-dialog .cp-mono { font-variant-numeric: tabular-nums; }

.cp-pagina .cp-nowrap,
.cp-dialog .cp-nowrap { white-space: nowrap; }

.cp-pagina .cp-difetto {
  color: var(--cp-difetto);
  font-weight: 700;
  white-space: nowrap;
}

.cp-pagina .cp-desc-cell,
.cp-dialog .cp-desc-cell {
  width: 100%;
  max-width: 0;
}

.cp-pagina .cp-desc,
.cp-dialog .cp-desc {
  display: block;
  font-size: .8rem;
  color: #555;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cp-pagina .p-datatable .p-datatable-thead > tr > th,
.cp-dialog .p-datatable .p-datatable-thead > tr > th { white-space: nowrap; }

@media (max-width: 767px) {
  .cp-pagina .cp-comandi > * { width: 100%; }
  .cp-pagina .cp-conto,
  .cp-pagina .cp-periodo { min-width: 0; }
  .cp-pagina .cp-cifra { font-size: 1.9rem; }
}

.cp-registro {
  min-width: min(34rem, 95vw);
  max-width: 95vw;
}

.cp-registro .p-dialog-content { overflow-x: auto; }
.cp-registro .p-datatable-table { width: auto; }

.cp-registro .p-datatable-thead > tr > th,
.cp-registro .p-datatable-tbody > tr > td { white-space: nowrap; }

.cp-registro .cp-registro-sub {
  font-size: .85rem;
  color: var(--cp-testo-quieto);
  margin: 0 0 1rem;
}

.cp-registro .cp-pill {
  display: inline-flex;
  align-items: center;
  font-size: .78rem;
  font-weight: 600;
  padding: .25rem .7rem;
  border-radius: 999px;
  white-space: nowrap;
}

.cp-registro .cp-pill-ok {
  background: rgba(51, 51, 51, .07);
  color: #44515f;
}

.cp-registro .cp-pill-ko {
  background: rgba(156, 75, 75, .1);
  color: var(--cp-difetto);
}
</style>
